import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Modal from "../Model";
import { useAuthz } from "../../auth/useAuthz";
import {
  CANCEL_ADMIN_PAYMENT_LINK,
  GET_ADMIN_PAYMENT_LINKS,
  GET_UNLINKED_RAZORPAY_PAYMENTS,
} from "./graphql";
import GeneratePaymentLinkForm from "./GeneratePaymentLinkForm";
import ResolveUnlinkedPaymentForm from "./ResolveUnlinkedPaymentForm";
import {
  inr,
  formatDateTime,
  PURPOSE_LABELS,
  SUGGESTION_LABELS,
  STATUS_STYLES,
  statusLabel,
} from "./format";

const UNLINKED_FILTERS = [
  "UNLINKED",
  "LINKED_BOOKING",
  "LINKED_EMPLOYEE_DEPOSIT",
  "OTHER_INCOME",
  "IGNORED",
  "ALL",
];
const LINK_FILTERS = ["", "CREATED", "PAID", "NEEDS_REVIEW", "EXPIRED", "CANCELLED"];

const StatusPill = ({ status }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-xs capitalize whitespace-nowrap ${
      STATUS_STYLES[status] || "bg-gray-100"
    }`}
  >
    {statusLabel(status)}
  </span>
);

const linkTarget = (l) => {
  if (l.purpose === "BOOKING_PAYMENT") return l.bookingId;
  if (l.purpose === "EMPLOYEE_CASH_DEPOSIT") {
    return `${l.employeeName || "Professional"} · ${l.orderIds?.length || 0} booking(s)`;
  }
  return l.category;
};

const resolvedTo = (p) => {
  if (p.linkedOrder) return `Booking ${p.linkedOrder.bookingId}`;
  if (p.linkedEmployeeName) return `Deposit · ${p.linkedEmployeeName}`;
  return p.resolutionNote || "";
};

/**
 * Razorpay links & payments: generate links with a purpose, and link the
 * Razorpay payments the webhook couldn't match (e.g. links created by hand
 * in the Razorpay dashboard) to bookings / professionals.
 */
const RazorpayPayments = () => {
  const { can } = useAuthz();
  const canEdit = can("payments.create");
  const [searchParams] = useSearchParams();
  const prefillBookingId = searchParams.get("bookingId");

  const [tab, setTab] = useState("unlinked");
  const [unlinkedStatus, setUnlinkedStatus] = useState("UNLINKED");
  const [linkStatus, setLinkStatus] = useState("");
  const [showGenerate, setShowGenerate] = useState(Boolean(prefillBookingId));
  const [resolving, setResolving] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const unlinkedQuery = useQuery(GET_UNLINKED_RAZORPAY_PAYMENTS, {
    variables: { data: { status: unlinkedStatus } },
    fetchPolicy: "network-only",
  });
  const linksQuery = useQuery(GET_ADMIN_PAYMENT_LINKS, {
    variables: { data: { status: linkStatus || undefined, limit: 100 } },
    fetchPolicy: "network-only",
  });
  const [cancelLink] = useMutation(CANCEL_ADMIN_PAYMENT_LINK);

  const unlinked = unlinkedQuery.data?.getUnlinkedRazorpayPayments;
  const links = linksQuery.data?.getAdminPaymentLinks?.links || [];

  const refetchAll = () => {
    unlinkedQuery.refetch();
    linksQuery.refetch();
  };

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.shortUrl);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (e) {
      setCopiedId(null);
    }
  };

  const handleCancel = async (link) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Cancel the ${inr(link.amount)} link? The payer can no longer use it.`)) return;
    const res = await cancelLink({ variables: { id: link.id } });
    const out = res.data?.cancelAdminPaymentLink;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") window.alert(out?.message || "Failed to cancel");
    linksQuery.refetch();
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold">Razorpay links & payments</h1>
            <p className="text-sm text-gray-500">
              Create payment links here instead of the Razorpay dashboard, so
              payments reach the right booking or professional automatically.
            </p>
          </div>
          {canEdit && (
            <button
              onClick={() => setShowGenerate(true)}
              className="px-5 py-2 bg-[#FBB033] text-black rounded-md font-semibold self-start sm:self-auto"
            >
              + Generate payment link
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setTab("unlinked")}
            className={`px-4 py-2 rounded ${
              tab === "unlinked" ? "bg-blue-600 text-white" : "bg-gray-200"
            }`}
          >
            Unlinked payments
            {unlinked?.unlinkedCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs">
                {unlinked.unlinkedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("links")}
            className={`px-4 py-2 rounded ${
              tab === "links" ? "bg-blue-600 text-white" : "bg-gray-200"
            }`}
          >
            Payment links
          </button>
        </div>

        {tab === "unlinked" && (
          <div className="bg-white rounded-lg border">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b">
              <p className="text-sm text-gray-600">
                Razorpay payments we couldn't match automatically.{" "}
                {unlinked?.unlinkedCount > 0 && (
                  <span className="font-semibold text-gray-900">
                    {unlinked.unlinkedCount} waiting · {inr(unlinked.unlinkedAmount)}
                  </span>
                )}
              </p>
              <select
                value={unlinkedStatus}
                onChange={(e) => setUnlinkedStatus(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm capitalize"
              >
                {UNLINKED_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2">Paid on</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Payer</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Created by</th>
                    <th className="px-3 py-2">Suggestion</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {unlinkedQuery.loading && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!unlinkedQuery.loading && !unlinked?.payments?.length && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                        Nothing here.
                      </td>
                    </tr>
                  )}
                  {unlinked?.payments?.map((p) => (
                    <tr key={p.id} className="border-t align-top">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDateTime(p.paidAt)}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {inr(p.amount)}
                      </td>
                      <td className="px-3 py-2">
                        <div>{p.contact || "—"}</div>
                        {p.vpa && <div className="text-xs text-gray-500">{p.vpa}</div>}
                      </td>
                      <td className="px-3 py-2">{p.description || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.linkCreatedBy === "API"
                          ? "App"
                          : p.linkCreatedBy
                          ? "Razorpay dashboard"
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {p.suggestedKind ? (
                          <>
                            <div>{SUGGESTION_LABELS[p.suggestedKind]}</div>
                            <div className="text-xs text-gray-500">
                              {p.suggestedOrder &&
                                `${p.suggestedOrder.bookingId} · ${p.suggestedOrder.customerName || ""}`}
                              {p.suggestedEmployeeName}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={p.status} />
                        {p.status !== "UNLINKED" && (
                          <div className="text-xs text-gray-500 mt-1">{resolvedTo(p)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canEdit && p.status === "UNLINKED" && (
                          <button
                            onClick={() => setResolving(p)}
                            className="px-3 py-1 bg-[#FBB033] rounded-md font-semibold"
                          >
                            Link
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "links" && (
          <div className="bg-white rounded-lg border">
            <div className="flex items-center justify-end p-3 border-b">
              <select
                value={linkStatus}
                onChange={(e) => setLinkStatus(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm capitalize"
              >
                {LINK_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    {s ? statusLabel(s) : "All statuses"}
                  </option>
                ))}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2">Created</th>
                    <th className="px-3 py-2">Purpose</th>
                    <th className="px-3 py-2">For</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Payer</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {linksQuery.loading && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!linksQuery.loading && links.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        No links yet.
                      </td>
                    </tr>
                  )}
                  {links.map((l) => (
                    <tr key={l.id} className="border-t align-top">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDateTime(l.createdAt)}
                      </td>
                      <td className="px-3 py-2">{PURPOSE_LABELS[l.purpose]}</td>
                      <td className="px-3 py-2">
                        <div>{linkTarget(l)}</div>
                        <div className="text-xs text-gray-500">{l.description}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{inr(l.amount)}</td>
                      <td className="px-3 py-2">
                        <div>{l.customerName}</div>
                        <div className="text-xs text-gray-500">{l.customerContact}</div>
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={l.status} />
                        {l.paidAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDateTime(l.paidAt)}
                          </div>
                        )}
                        {l.processingNote && (
                          <div className="text-xs text-red-600 mt-1">{l.processingNote}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                        {l.status === "CREATED" && l.shortUrl && (
                          <button
                            onClick={() => copy(l)}
                            className="px-3 py-1 border rounded-md"
                          >
                            {copiedId === l.id ? "Copied" : "Copy link"}
                          </button>
                        )}
                        {canEdit && l.status === "CREATED" && (
                          <button
                            onClick={() => handleCancel(l)}
                            className="px-3 py-1 border rounded-md text-red-600"
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showGenerate && (
          <Modal onClose={() => setShowGenerate(false)} disableOutsideClick>
            <GeneratePaymentLinkForm
              initialBookingId={prefillBookingId}
              onClose={() => setShowGenerate(false)}
              onCreated={() => {
                setTab("links");
                linksQuery.refetch();
              }}
            />
          </Modal>
        )}

        {resolving && (
          <Modal onClose={() => setResolving(null)} disableOutsideClick>
            <ResolveUnlinkedPaymentForm
              payment={resolving}
              onClose={() => setResolving(null)}
              onResolved={refetchAll}
            />
          </Modal>
        )}
      </Layout>
    </div>
  );
};

export default RazorpayPayments;
